import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { migrationPresets } from 'utils/storeMigration/runMigration.js';
import DashboardLayout from 'layouts/DashboardLayout'
import DashboardNavbar from 'components/RDNavbars/DashboardNavbar';

const MigrationRunner = () => {
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [logs, setLogs] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('dryRun');
  const [clientId, setClientId] = useState('grm');
  const logsEndRef = useRef(null);

  // Auto-scroll logs to bottom
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Override console.log to capture logs in UI
  const originalConsoleLog = console.log;
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  const addLog = (level, message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { level, message, timestamp }]);
  };

  const startMigration = async () => {
    setIsRunning(true);
    setResults(null);
    setLogs([]);

    // Intercept console output
    console.log = (...args) => {
      originalConsoleLog(...args);
      addLog('info', args.join(' '));
    };
    console.error = (...args) => {
      originalConsoleError(...args);
      addLog('error', args.join(' '));
    };
    console.warn = (...args) => {
      originalConsoleWarn(...args);
      addLog('warn', args.join(' '));
    };

    try {
      addLog('info', `🚀 Starting ${selectedPreset} migration for client: ${clientId}`);
      
      const result = await migrationPresets[selectedPreset](clientId);
      
      setResults(result);
      
      if (result.success) {
        addLog('info', '🎉 Migration completed successfully!');
      } else {
        addLog('error', `❌ Migration failed: ${result.error}`);
      }
      
    } catch (error) {
      addLog('error', `💥 Unexpected error: ${error.message}`);
      setResults({ success: false, error: error.message });
    } finally {
      // Restore console
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      
      setIsRunning(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
    setResults(null);
  };

  const getLogIcon = (level) => {
    switch (level) {
      case 'error': return '❌';
      case 'warn': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '📝';
    }
  };

  const getLogColor = (level) => {
    switch (level) {
      case 'error': return 'text-red-600';
      case 'warn': return 'text-yellow-600';
      case 'info': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Hierarchy Migration Runner
          </h1>
          <p className="text-gray-600">
            Migrate legacy area/store data to new hierarchy schema. 
            Existing reports will continue working unchanged.
          </p>
        </div>

        {/* Migration Controls */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">Migration Configuration</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Client ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client ID
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isRunning}
              />
            </div>

            {/* Migration Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Migration Type
              </label>
              <select
                value={selectedPreset}
                onChange={(e) => setSelectedPreset(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isRunning}
              >
                <option value="dryRun">Dry Run (Preview Only)</option>
                <option value="development">Development (With Validation)</option>
                <option value="production">Production (Full Validation)</option>
                <option value="quick">Quick (Minimal Validation)</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex items-end space-x-2">
              <button
                onClick={startMigration}
                disabled={isRunning}
                className={`px-4 py-2 rounded-md font-medium ${
                  isRunning
                    ? 'bg-gray-400 cursor-not-allowed'
                    : selectedPreset === 'dryRun'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-green-600 hover:bg-green-700'
                } text-white transition-colors`}
              >
                {isRunning ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Running...
                  </span>
                ) : (
                  selectedPreset === 'dryRun' ? '🔍 Preview Migration' : '🚀 Run Migration'
                )}
              </button>
              
              <button
                onClick={clearLogs}
                disabled={isRunning}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium transition-colors disabled:bg-gray-400"
              >
                Clear Logs
              </button>
            </div>
          </div>

          {/* Migration Type Info */}
          <div className="bg-gray-50 rounded-md p-4">
            <h3 className="font-medium text-gray-900 mb-2">Migration Types:</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div><strong>Dry Run:</strong> Preview what would happen without making any changes</div>
              <div><strong>Development:</strong> Full migration with validation (recommended for first run)</div>
              <div><strong>Production:</strong> Maximum safety with comprehensive validation</div>
              <div><strong>Quick:</strong> Fast migration with minimal validation (for testing)</div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {results && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Migration Results</h2>
            
            <div className={`p-4 rounded-md ${
              results.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className={`font-medium ${
                results.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {results.success ? '✅ Migration Successful' : '❌ Migration Failed'}
              </div>
              
              {results.migrationResults && (
                <div className="mt-3 text-sm space-y-1">
                  <div>OrgUnits Created: {results.migrationResults.areas?.created || 0}</div>
                  <div>StoreRecords Updated: {results.migrationResults.stores?.created || 0}</div>
                  <div>Manager Assignments: {results.migrationResults.managers?.created || 0}</div>
                </div>
              )}
              
              {results.error && (
                <div className="mt-2 text-sm text-red-700">
                  Error: {results.error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Live Logs */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Migration Logs</h2>
            <div className="text-sm text-gray-500">
              {logs.length} log entries
            </div>
          </div>
          
          <div className="bg-gray-900 rounded-md p-4 h-96 overflow-y-auto font-mono text-sm">
            {logs.length === 0 ? (
              <div className="text-gray-400 italic">
                Logs will appear here when migration runs...
              </div>
            ) : (
              <div className="space-y-1">
                {logs.map((log, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <span className="text-gray-400 text-xs min-w-[60px]">
                      {log.timestamp}
                    </span>
                    <span className="text-xs">
                      {getLogIcon(log.level)}
                    </span>
                    <span className={`${getLogColor(log.level)} text-white`}>
                      {log.message}
                    </span>
                  </div>
                ))}
                <div ref={logsEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Migration Tools */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">Migration Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">🎯 Store-by-Store Migration</h3>
              <p className="text-sm text-gray-600 mb-3">
                Manually review and migrate each store with full data validation. 
                Recommended for first-time migration.
              </p>
              <button
                onClick={() => navigate('/grm/temp-pages/store-migration-manager')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm"
              >
                Open Store Migration Manager
              </button>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">🚀 Bulk Migration</h3>
              <p className="text-sm text-gray-600 mb-3">
                Run the complete migration process for all stores at once. 
                Use after validating with store-by-store tool.
              </p>
              <p className="text-xs text-gray-500">Use the controls above to run bulk migration</p>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        {results?.success && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-medium text-blue-900 mb-2">🎉 Next Steps</h3>
            <div className="text-blue-800 text-sm space-y-1">
              <div>1. Verify existing reports still work correctly</div>
              <div>2. Test new hierarchy data is available in hooks</div>
              <div>3. Proceed to Step 2: Hook Refactoring</div>
              <div>4. Build admin tools for self-service store management</div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MigrationRunner;